import {Box, Label, Link} from '@primer/react'
import {userHovercardPath} from '@github-ui/paths'
import {LABELS} from '../../constants'
import {GitHubAvatar} from '@github-ui/github-avatar'
import {graphql} from 'relay-runtime'
import {useFragment} from 'react-relay'
import type {RepositoryPill$key} from './__generated__/RepositoryPill.graphql'
import type {BetterSystemStyleObject} from '@primer/react/lib-esm/sx'

const RepositoryPillFragment = graphql`
  fragment RepositoryPill on Issue {
    repository {
      name
      owner {
        login
        avatarUrl(size: 64)
        ownerType: __typename
      }
      isPrivate
    }
  }
`

export type RepositoryPillProps = {
  repositoryPillData: RepositoryPill$key
  sx?: BetterSystemStyleObject
}

export function RepositoryPill({repositoryPillData, sx}: RepositoryPillProps) {
  const {repository} = useFragment(RepositoryPillFragment, repositoryPillData)
  const repoOwner = repository.owner.login
  const repoName = repository.name

  return (
    <Box
      sx={{
        color: 'fg.subtle',
        fontWeight: 'normal',
        fontSize: 0,
        display: 'flex',
        gap: 1,
        justifyContent: 'flex-start',
        alignItems: 'center',
        flexDirection: 'row',
        flexWrap: 'nowrap',
        ml: [1, 1, 2, 2],
        overflow: 'hidden',
        whiteSpace: 'nowrap',
        ...sx,
      }}
    >
      <Box sx={{display: 'flex', flexShrink: 1, overflow: 'hidden'}}>
        <Link
          href={`/${repoOwner}`}
          sx={{
            color: 'fg.muted',
            fontWeight: 'normal',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            flexBasis: 'auto',
            flexShrink: 1,
          }}
          data-hovercard-url={userHovercardPath({owner: repoOwner})}
        >
          <GitHubAvatar
            square={repository.owner.ownerType === 'Organization'}
            src={repository.owner.avatarUrl}
            size={16}
            alt={`${repoOwner}`}
            sx={{mr: 1}}
          />
          {repoOwner}
        </Link>
        {'/'}
        <Link
          href={`/${repoOwner}/${repoName}`}
          sx={{
            color: 'fg.muted',
            fontWeight: 'normal',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            flexBasis: 'auto',
            flexShrink: 4,
          }}
        >
          {repoName}
        </Link>
      </Box>
      <Label data-watch-overflow sx={{ml: 1, color: 'fg.default'}}>
        {repository.isPrivate ? LABELS.private : LABELS.public}
      </Label>
    </Box>
  )
}

try{ RepositoryPill.displayName ||= 'RepositoryPill' } catch {}